// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Glint — the bots. Used by the balance/quality sim (principle #18, is spotting
// load-bearing and is the mode's rule load-bearing?) and by the browser-verification hook
// (drive a real run without a human). A policy is a reaction-time distribution plus an
// error rate; a BLIND policy ignores the shared symbol entirely and taps at random, which
// is the control that proves reading the cards is the whole game.

import { currentPair, type GameState } from './game';

export interface Policy {
  readonly id: string;
  readonly meanRtMs: number; // mean time to spot & tap the shared symbol
  readonly jitterMs: number; // ± uniform jitter on the reaction time
  readonly errorRate: number; // chance of tapping a wrong symbol instead
  readonly blind: boolean; // ignore the shared symbol entirely (random tap)
}

export const READER: Policy = { id: 'reader', meanRtMs: 620, jitterMs: 260, errorRate: 0.015, blind: false };
export const CAREFUL: Policy = { id: 'careful', meanRtMs: 900, jitterMs: 260, errorRate: 0.006, blind: false };
export const FAST_SLOPPY: Policy = { id: 'fast', meanRtMs: 360, jitterMs: 170, errorRate: 0.11, blind: false };
export const BLIND: Policy = { id: 'blind', meanRtMs: 420, jitterMs: 200, errorRate: 1, blind: true };

export const POLICIES: Policy[] = [READER, CAREFUL, FAST_SLOPPY, BLIND];

/** A reaction time for the next tap (never negative). */
export function drawReaction(p: Policy, rnd: () => number): number {
  return Math.max(90, p.meanRtMs + (rnd() * 2 - 1) * p.jitterMs);
}

/** A random symbol on a card. */
function pickFrom(card: readonly number[], rnd: () => number): number {
  return card[Math.floor(rnd() * card.length)];
}

/** A random symbol that is NOT the shared answer (from either card in the pair). */
function pickWrong(top: readonly number[], bottom: readonly number[], shared: number, rnd: () => number): number {
  const wrong = [...top, ...bottom].filter((s) => s !== shared);
  return wrong.length ? wrong[Math.floor(rnd() * wrong.length)] : shared;
}

/**
 * The symbol this policy taps for the current pair, right now. A blind policy taps a random
 * symbol on the top card (so it is correct with probability 1/(n+1)); otherwise it taps the
 * shared symbol, except on an error where it taps a random wrong one.
 */
export function pickTap(s: GameState, p: Policy, rnd: () => number): number {
  const { top, bottom, shared } = currentPair(s);
  if (p.blind) return pickFrom(top, rnd);
  if (rnd() < p.errorRate) return pickWrong(top, bottom, shared, rnd);
  return shared;
}
